import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Play, Download, AlertCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { OptimizationService } from '@/services/OptimizationService';
import { type TradingFile } from '@/services/TradingPatternAnalysisService';
import { AnalysisMode } from '@/types/trading-analysis-types';
import * as d3 from 'd3-scale-chromatic';

interface HeatmapCell {
  threshold: number;
  sequenceLength: number;
  accuracy: number;
  totalPredictions: number;
  skipRate: number;
}

interface HeatmapData {
  cells: HeatmapCell[];
  bestConfiguration: HeatmapCell;
  processingTime: number;
}

interface PerformanceHeatmapProps {
  selectedFile: TradingFile | null;
  selectedAnalysisMode: AnalysisMode;
}

export const PerformanceHeatmap: React.FC<PerformanceHeatmapProps> = ({
  selectedFile,
  selectedAnalysisMode
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [heatmapData, setHeatmapData] = useState<HeatmapData | null>(null);
  const [hoveredCell, setHoveredCell] = useState<HeatmapCell | null>(null);

  // Configuration
  const thresholdRange = [0.50, 0.90];
  const thresholdStep = 0.05; // 5% steps
  const sequenceLengthRange = [3, 12];

  const thresholds = [];
  for (let t = thresholdRange[0]; t <= thresholdRange[1]; t += thresholdStep) {
    thresholds.push(Math.round(t * 100) / 100);
  }

  const sequenceLengths = [];
  for (let s = sequenceLengthRange[0]; s <= sequenceLengthRange[1]; s++) {
    sequenceLengths.push(s);
  }

  const handleGenerateHeatmap = async () => {
    if (!selectedFile?.id) {
      toast.error('Please select a file first');
      return;
    }

    setIsGenerating(true);
    setProgress(0);
    setHeatmapData(null);

    try {
      const result = await OptimizationService.runPerformanceHeatmap({
        fileId: selectedFile.id,
        thresholdRange: thresholdRange as [number, number],
        thresholdStep,
        sequenceLengthRange: sequenceLengthRange as [number, number],
        analysisMode: selectedAnalysisMode
      }, (progressUpdate) => {
        setProgress((progressUpdate.completed / progressUpdate.total) * 100);
      });

      if (result.success && result.data) {
        setHeatmapData(result.data);
        toast.success('Heatmap generated successfully!');
      } else {
        toast.error(result.error || 'Failed to generate heatmap');
      }
    } catch (error) {
      console.error('Heatmap generation error:', error);
      toast.error('Failed to generate heatmap');
    } finally {
      setIsGenerating(false);
      setProgress(0);
    }
  };

  const getColorForAccuracy = (accuracy: number): string => {
    // Normalize accuracy (0-100) to (0-1) for color scale
    const normalized = Math.max(0, Math.min(1, accuracy / 100));
    // Use RdYlGn color scheme: Red (low) → Yellow (medium) → Green (high)
    return d3.interpolateRdYlGn(normalized);
  };

  const getCellSize = () => {
    const containerWidth = 600;
    const cellWidth = containerWidth / thresholds.length;
    const cellHeight = Math.min(40, cellWidth);
    return { width: cellWidth, height: cellHeight };
  };

  const renderHeatmapGrid = () => {
    if (!heatmapData) return null;

    const { width: cellWidth, height: cellHeight } = getCellSize();

    return (
      <div className="space-y-4">
        {/* Best Configuration Banner */}
        {heatmapData.bestConfiguration && (
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="pt-4">
              <div className="flex items-center gap-4">
                <TrendingUp className="h-5 w-5 text-primary" />
                <div className="flex-1">
                  <div className="font-medium">Optimal Configuration Found</div>
                  <div className="text-sm text-muted-foreground">
                    Threshold: {(heatmapData.bestConfiguration.threshold * 100).toFixed(0)}% | 
                    Sequence: {heatmapData.bestConfiguration.sequenceLength} | 
                    Accuracy: {heatmapData.bestConfiguration.accuracy.toFixed(1)}%
                  </div>
                </div>
                <Badge variant="default">
                  {heatmapData.bestConfiguration.accuracy.toFixed(1)}% Accuracy
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Heatmap Visualization */}
        <div className="space-y-2">
          {/* Y-axis labels (Sequence Lengths) */}
          <div className="flex">
            <div className="w-16 flex items-center justify-center text-sm font-medium">
              Sequence
            </div>
            {/* X-axis labels (Thresholds) */}
            <div className="flex-1 grid" style={{ gridTemplateColumns: `repeat(${thresholds.length}, 1fr)` }}>
              {thresholds.map((threshold) => (
                <div key={threshold} className="text-center text-xs font-medium py-1">
                  {(threshold * 100).toFixed(0)}%
                </div>
              ))}
            </div>
          </div>

          {/* Heatmap Grid */}
          {sequenceLengths.map((seqLen) => (
            <div key={seqLen} className="flex">
              {/* Y-axis label */}
              <div className="w-16 flex items-center justify-center text-sm font-medium">
                {seqLen}
              </div>
              
              {/* Row of cells */}
              <div className="flex-1 grid gap-1" style={{ gridTemplateColumns: `repeat(${thresholds.length}, 1fr)` }}>
                {thresholds.map((threshold) => {
                  const cell = heatmapData.cells.find(
                    c => c.threshold === threshold && c.sequenceLength === seqLen
                  );
                  
                  if (!cell) {
                    return (
                      <div
                        key={`${threshold}-${seqLen}`}
                        className="border border-border bg-muted/20"
                        style={{ height: `${cellHeight}px` }}
                      />
                    );
                  }

                  const isOptimal = heatmapData.bestConfiguration && 
                    cell.threshold === heatmapData.bestConfiguration.threshold &&
                    cell.sequenceLength === heatmapData.bestConfiguration.sequenceLength;

                  return (
                    <TooltipProvider key={`${threshold}-${seqLen}`}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div
                            className={`border cursor-pointer transition-all duration-200 ${
                              isOptimal ? 'border-primary border-2 shadow-md' : 'border-border'
                            } ${hoveredCell === cell ? 'scale-105 z-10 shadow-lg' : ''}`}
                            style={{
                              height: `${cellHeight}px`,
                              backgroundColor: getColorForAccuracy(cell.accuracy)
                            }}
                            onMouseEnter={() => setHoveredCell(cell)}
                            onMouseLeave={() => setHoveredCell(null)}
                          >
                            <div className="h-full flex items-center justify-center text-xs font-medium text-white mix-blend-difference">
                              {cell.accuracy.toFixed(0)}%
                            </div>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="p-3">
                          <div className="space-y-1">
                            <div className="font-medium">
                              Threshold: {(cell.threshold * 100).toFixed(0)}% | Sequence: {cell.sequenceLength}
                            </div>
                            <div className="text-sm space-y-0.5">
                              <div>Accuracy: {cell.accuracy.toFixed(1)}%</div>
                              <div>Predictions: {cell.totalPredictions}</div>
                              <div>Skip Rate: {cell.skipRate.toFixed(1)}%</div>
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-4 pt-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getColorForAccuracy(0) }} />
            <span className="text-xs">Low Accuracy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getColorForAccuracy(50) }} />
            <span className="text-xs">Medium Accuracy</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 rounded" style={{ backgroundColor: getColorForAccuracy(100) }} />
            <span className="text-xs">High Accuracy</span>
          </div>
        </div>

        {/* X-axis title */}
        <div className="text-center text-sm font-medium text-muted-foreground pt-2">
          Win Threshold (%)
        </div>
      </div>
    );
  };

  if (!selectedFile) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
        <p className="text-lg font-medium mb-2">No File Selected</p>
        <p className="text-sm text-muted-foreground">
          Please select a trading file to generate performance heatmap
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Performance Heatmap</h3>
          <p className="text-sm text-muted-foreground">
            Visualize accuracy across all threshold and sequence length combinations
          </p>
        </div>
        <Badge variant="outline">File: {selectedFile.filename}</Badge>
      </div>

      {/* Configuration & Generate */}
      <Card>
        <CardHeader>
          <CardTitle>Heatmap Configuration</CardTitle>
          <CardDescription>
            Generate 2D performance visualization across {thresholds.length} thresholds × {sequenceLengths.length} sequence lengths
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="font-medium">Threshold Range</div>
              <div className="text-muted-foreground">
                {(thresholdRange[0] * 100).toFixed(0)}% - {(thresholdRange[1] * 100).toFixed(0)}% (step: {(thresholdStep * 100).toFixed(0)}%)
              </div>
            </div>
            <div>
              <div className="font-medium">Sequence Range</div>
              <div className="text-muted-foreground">
                {sequenceLengthRange[0]} - {sequenceLengthRange[1]} trades
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              Total combinations: {thresholds.length * sequenceLengths.length}
            </div>
            <Button 
              onClick={handleGenerateHeatmap} 
              disabled={isGenerating}
            >
              <Play className="h-4 w-4 mr-2" />
              {isGenerating ? 'Generating...' : 'Generate Heatmap'}
            </Button>
          </div>

          {isGenerating && (
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Processing combinations...</span>
                <span>{progress.toFixed(0)}%</span>
              </div>
              <Progress value={progress} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Heatmap Results */}
      {heatmapData && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Performance Heatmap</CardTitle>
                <CardDescription>
                  Interactive visualization - hover for details, optimal configuration highlighted
                </CardDescription>
              </div>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {renderHeatmapGrid()}
          </CardContent>
        </Card>
      )}
    </div>
  );
};